import React, { useContext, useRef } from 'react';
import { MobXProviderContext, observer } from 'mobx-react';

import classNames from 'classnames';

import '../../../css/QuestionFlyout.scss';

import videoService from '../../services/VideoService';
import audioService from '../../services/AudioService';

import useAccessibilityClick from '../../../hooks/useAccessibilityClick';

import ActivityChildViewer from '../activityViewer/ActivityChildViewer';
import StandAloneViewer from '../StandAloneViewer';
import navMenuService from '../../services/NavMenuService';
import rightRailService, { RightRailTab } from '../../services/RightRailService';
import { register } from '../../../i18n';

const t = register('PanelMenu');

const QuestionFlyout = observer(({
  // TODO unused // model,
  isActivity,
  lessonElementId,
  showQuestionBanner,
  variant = 'full'
}) => {
  const {
    lessonManager, videoManager, audioManager
  } = useContext(MobXProviderContext);

  const openButton = useRef();
  const childModel = lessonManager.getSlideModel(lessonElementId);

  const parentModel = lessonManager.getSlideModel(childModel?.parentActivityElementId);

  lessonManager.setIsQuestionListView(false);

  useAccessibilityClick(openButton, () => {
    if (variant === 'tabs') {
      rightRailService.toggleInteractive();
      return;
    }
    navMenuService.toggleInteractiveFlyout();
  });

  const isVideo = parentModel.type.includes('Video');
  const isAudio = parentModel.type.includes('Audio');

  const isDividedLayoutVideoQuestion = videoService.isDividedLayoutVideoQuestion(parentModel);
  const isDividedLayoutAudioQuestion = audioService.isDividedLayoutAudioQuestion(parentModel);
  let customVideoContentLabel, customAudioContentLabel;

  if (isDividedLayoutVideoQuestion) {
    customVideoContentLabel = videoService.getCustomVideoLayoutLabel({
      model: parentModel,
      videoLabelPrefix: 'q-flyout-wrapper'
    });
    if (!lessonManager.isInteractiveFlyoutOpen) {
      lessonManager.setIsInteractiveFlyoutOpen(true);
    }
  }
  if (isDividedLayoutAudioQuestion) {
    customAudioContentLabel = audioService.getCustomAudioLayoutLabel({
      audioLabelPrefix: 'q-flyout-wrapper',
      model: parentModel
    });
    if (!lessonManager.isInteractiveFlyoutOpen) {
      lessonManager.setIsInteractiveFlyoutOpen(true);
    }
  }

  const activityLessonElement = lessonManager.getLessonElement(childModel.parentActivityElementId);
  const groupListIds = activityLessonElement?.lessonElementGroups?.get?.(childModel.groupId) || [];
  const questionsOpen = (variant === 'tabs')
    ? rightRailService.selectedTab === RightRailTab.INTERACTIVE
    : lessonManager.isInteractiveFlyoutOpen;

  const questionBody = ((isVideo && (!isDividedLayoutVideoQuestion || videoManager.allowShowDividedVideoQuestions)) ||
    (isAudio && (!isDividedLayoutAudioQuestion || audioManager.allowShowDividedAudioQuestions))) ? (
      <>
        {(groupListIds?.length || parentModel?.showAllChildren) && (
          <ActivityChildViewer
            lessonElementId={lessonElementId}
            model={childModel}
            showAllChildren={parentModel?.showAllChildren}
            showQuestionBanner={showQuestionBanner} />
        )}
        {!groupListIds?.length && !parentModel?.showAllChildren && (
          <StandAloneViewer
            lessonElementId={lessonElementId}
            model={childModel}
            showQuestionBanner={showQuestionBanner} />
        )}
      </>
    ) : null;

  if (!lessonElementId) {
    return <></>;
  }

  if (variant === 'tabs') {
    // Video and audio keep the question button in .q-flyout-tab-bar. Tab size
    // rules are written against that wrapper, so the rail has to use it too.
    const useQuestionTabBar = (isVideo && !isDividedLayoutVideoQuestion) || (isAudio && !isDividedLayoutAudioQuestion);
    return (
      <div className={classNames('q-flyout-wrapper', 'right-rail-question-tabs', (isAudio && customAudioContentLabel), (isVideo && customVideoContentLabel), {
        activity: isActivity,
        closed: !questionsOpen,
        wide: questionsOpen
      }, parentModel.type)}>
        <div className={classNames({ 'q-flyout-tab-bar': useQuestionTabBar })}>
          <div ref={openButton}
            aria-controls='right-rail-panel'
            aria-expanded={questionsOpen}
            aria-label={(!questionsOpen) ? `${t('openMenu', 'Open flyout menu')}` : `${t('closeMenu', 'Close flyout menu')}`}
            aria-selected={questionsOpen}
            className={classNames('q-flyout-tab', 'open-button-wrapper', {
              'q-flyout-tab-closed': !questionsOpen,
              selected: questionsOpen
            })}
            role='tab'
            tabIndex='0'>
            <div aria-hidden='true' className='icon-wrapper'>
              <div aria-hidden='true' className='open-button' />
            </div>
          </div>
        </div>
      </div>
    );
  }

  if (variant === 'panel') {
    return (
      <div className={classNames('q-flyout-panel', 'right-rail-question-panel', {
        'audio-q-flyout-panel': isDividedLayoutAudioQuestion,
        'video-q-flyout-panel': isDividedLayoutVideoQuestion
      })}>
        {questionBody}
      </div>
    );
  }

  return (
    <div className={classNames('q-flyout-wrapper', (isAudio && customAudioContentLabel), (isVideo && customVideoContentLabel), {
      activity: isActivity,
      closed: (!lessonManager.isInteractiveFlyoutOpen),
      wide: (lessonManager.isInteractiveFlyoutOpen)
    }, parentModel.type)}>
      <div className={classNames({
        'q-flyout-tab-bar': (isVideo && !isDividedLayoutVideoQuestion) || (isAudio && !isDividedLayoutAudioQuestion)
      })}>
        <div ref={openButton}
          aria-expanded={lessonManager.isInteractiveFlyoutOpen}
          aria-label={(!lessonManager.isInteractiveFlyoutOpen) ? `${t('openMenu', 'Open flyout menu')}` : `${t('closeMenu', 'Close flyout menu')}`}
          className={classNames('q-flyout-tab open-button-wrapper', {
            'q-flyout-tab-closed': !lessonManager.isInteractiveFlyoutOpen
          })}
          role='button'
          tabIndex='0'>
          <div aria-hidden='true' className='icon-wrapper'>
            <div aria-hidden='true' className='open-button' />
          </div>
        </div>
      </div>
      <div className='q-flyout-panel-wrapper'>
        <div className={classNames('q-flyout-panel', {
          'audio-q-flyout-panel': isDividedLayoutAudioQuestion,
          'video-q-flyout-panel': isDividedLayoutVideoQuestion
        })}>
          {questionBody}
        </div>
      </div>
    </div>
  );
});

export default QuestionFlyout;
